import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Megaphone, CheckCircle2, ArrowRight, Eye, MousePointerClick } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const BannerAds = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [myRests, setMyRests] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [form, setForm] = useState({
    restaurant_id: "",
    package_id: "",
    headline: "",
    cta_text: "Lihat Restoran",
    image_url: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    try {
      const [p, m, ads] = await Promise.all([
        api.get("/ads/packages"),
        api.get("/restaurants/owner/me"),
        api.get("/ads/my"),
      ]);
      setPackages(p.data);
      setMyRests(m.data);
      setMyAds(ads.data);
      if (m.data[0] && !form.restaurant_id) setForm(f => ({ ...f, restaurant_id: m.data[0].id, image_url: m.data[0].cover_image || "" }));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!user) navigate("/");
    else loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onImageUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd);
      setForm({ ...form, image_url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` });
      toast.success("Gambar diupload");
    } catch { toast.error("Upload gagal"); }
  };

  const purchase = async () => {
    if (!form.restaurant_id || !form.package_id || !form.image_url) {
      toast.error("Lengkapi semua field (restoran, paket, gambar)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/ads/checkout", {
        package_id: form.package_id,
        restaurant_id: form.restaurant_id,
        origin_url: window.location.origin,
        headline: form.headline,
        cta_text: form.cta_text,
        image_url: form.image_url,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat checkout");
      setSubmitting(false);
    }
  };

  const formatIDR = (n) => `Rp ${n.toLocaleString("id-ID")}`;
  const formatDateID = (iso) => new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  const isActive = (until) => until && new Date(until) > new Date();

  return (
    <div data-testid="banner-ads-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Paid Promotion</p>
        <h1 className="font-heading text-3xl font-black mt-2">Banner Ads</h1>
        <p className="text-muted-foreground mt-2">Pasang banner iklan di homepage atau hasil pencarian untuk traffic maksimal</p>
      </div>

      {myRests.length === 0 ? (
        <div className="warm-card p-12 text-center">
          <p className="text-muted-foreground mb-4">Daftarkan restoranmu dulu sebelum membeli banner ads.</p>
          <Link to="/dashboard/restaurants/new"><Button className="rounded-full">Tambah Restoran</Button></Link>
        </div>
      ) : (
        <>
          {/* Package Pricing */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {packages.map(pkg => (
              <button
                key={pkg.id}
                onClick={() => setForm({ ...form, package_id: pkg.id })}
                data-testid={`ad-pkg-${pkg.id}`}
                className={`warm-card p-6 text-left transition-all ${form.package_id === pkg.id ? "ring-2 ring-primary border-primary" : ""}`}
              >
                <Megaphone className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-heading font-black text-lg">{pkg.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{pkg.tagline}</p>
                <div className="font-heading text-2xl font-black mt-4">{formatIDR(pkg.amount)}</div>
                <div className="text-xs text-muted-foreground">{pkg.duration_days} hari</div>
                {form.package_id === pkg.id && (
                  <div className="flex items-center gap-1 mt-3 text-primary text-xs font-bold">
                    <CheckCircle2 className="w-4 h-4" /> Terpilih
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Ad Creative Form */}
          <div className="warm-card p-6 mb-8">
            <h2 className="font-heading text-xl font-black mb-5">Desain Banner</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Pilih Restoran *</Label>
                <Select
                  value={form.restaurant_id}
                  onValueChange={v => {
                    const rest = myRests.find(r => r.id === v);
                    setForm({ ...form, restaurant_id: v, image_url: rest?.cover_image || form.image_url });
                  }}
                >
                  <SelectTrigger data-testid="ad-restaurant-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {myRests.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>CTA Button Text</Label>
                <Input
                  data-testid="ad-cta-input"
                  value={form.cta_text}
                  onChange={e => setForm({ ...form, cta_text: e.target.value })}
                  placeholder="Pesan Sekarang / Lihat Menu"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Headline (opsional)</Label>
                <Input
                  data-testid="ad-headline-input"
                  value={form.headline}
                  onChange={e => setForm({ ...form, headline: e.target.value })}
                  placeholder="Contoh: Diskon 50% Menu Spesial - Hari Ini Saja!"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Gambar Banner *</Label>
                {form.image_url && <img src={form.image_url} alt="" className="w-full max-w-md h-40 object-cover rounded-lg mt-2 mb-2" />}
                <Input type="file" accept="image/*" onChange={onImageUpload} data-testid="ad-image-upload" />
                <p className="text-xs text-muted-foreground mt-1">Rekomendasi: gambar landscape 1200x400px</p>
              </div>
            </div>

            {/* Preview */}
            {form.image_url && (
              <div className="mt-6">
                <Label>Preview</Label>
                <div className="relative h-48 md:h-64 overflow-hidden rounded-2xl border border-border mt-2">
                  <img src={form.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between text-white">
                    <span className="self-start text-[10px] tracking-[0.2em] uppercase font-bold bg-white/20 backdrop-blur px-2 py-1 rounded-full">Sponsored</span>
                    <div>
                      <h3 className="font-heading font-black text-2xl md:text-4xl leading-tight">
                        {form.headline || myRests.find(r => r.id === form.restaurant_id)?.name || "Preview"}
                      </h3>
                      <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold bg-primary px-3 py-1.5 rounded-full">
                        {form.cta_text || "Lihat Restoran"} →
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <Button
              onClick={purchase}
              disabled={submitting || !form.package_id || !form.image_url}
              className="mt-6 rounded-full bg-primary hover:bg-primary/90"
              data-testid="ad-buy-btn"
            >
              Beli Banner Ad <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          {/* My Ads */}
          <div className="warm-card p-6">
            <h2 className="font-heading text-xl font-black mb-4">Banner Saya</h2>
            {myAds.length === 0 ? (
              <p className="text-sm text-muted-foreground">Belum ada banner ads.</p>
            ) : (
              <div className="divide-y divide-border" data-testid="my-ads-list">
                {myAds.map(a => (
                  <div key={a.id} className="py-3 flex items-center gap-4" data-testid={`ad-${a.id}`}>
                    <img src={a.image_url} alt="" className="w-20 h-14 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{a.headline || a.restaurant?.name}</div>
                      <div className="text-xs text-muted-foreground">{a.placement} • {a.duration_days} hari</div>
                      <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{a.impressions || 0}</span>
                        <span className="flex items-center gap-1"><MousePointerClick className="w-3 h-3" />{a.clicks || 0}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={isActive(a.active_until) ? "bg-green-600" : a.status === "pending_payment" ? "bg-amber-500" : "bg-muted"}>
                        {isActive(a.active_until) ? "AKTIF" : a.status === "pending_payment" ? "MENUNGGU BAYAR" : "BERAKHIR"}
                      </Badge>
                      {a.active_until && (
                        <div className="text-[10px] text-muted-foreground mt-1">s/d {formatDateID(a.active_until)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BannerAds;
